import { RESTClient, RESTClientMock } from '@api/client';
import { API_URL } from '@api/apiDictionary';
import { fetchQCTaskMock } from '@api/mocks/qcTask';
import { USE_API_MOCKS } from '../env';
import { fetchQCTaskUploadsMock } from '@api/mocks/qcTaskUploads';
import { QCUploadEDTF } from '@dynamika-shared/types/ImagingSpecialist';
import { QCTaskUpload } from '@dynamika-shared/types/qcTaskUpload';
import { QCTaskComment } from '@restore/QCTaskComments/QCTaskComments.types';
import {
  addQCTaskCommentsMock,
  fetchQCTaskCommentsMock,
} from '@api/mocks/qcTaskComments';
import { fetchQCTaskSeriesMock } from '@api/mocks/qcTaskSeries';
import { fetchQCTaskUploadMock } from '@api/mocks/qcTaskUpload';
import {
  PatientUploadStatusEnum,
  QcStatusEnum,
} from '@dynamika-shared/types/status';
import { QCTask } from '@dynamika-shared/types/task';
import { QCVisitWithSeries } from '@dynamika-shared/types/visits';
import { QCTaskPatient } from '@dynamika-shared/types/patient';
import { replacePathParams } from '@utils/replacePathWithParams';
const api = USE_API_MOCKS ? RESTClientMock : RESTClient;

export const fetchQCTask = async (clinicalTrialId: string, taskId: string) => {
  if (USE_API_MOCKS) {
    await fetchQCTaskMock(taskId);
  }

  return api.get<QCTask>(API_URL.qcTask.replace(':taskId', taskId), {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
    id: 'qc-task',
  });
};

export const fetchQCTaskUploads = async (
  clinicalTrialId: string,
  taskId: string
) => {
  if (USE_API_MOCKS) {
    await fetchQCTaskUploadsMock(taskId);
  }

  return api.get<{ uploads: QCTaskUpload[] }>(
    API_URL.qcTaskUploads.replace(':taskId', taskId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-uploads',
    }
  );
};

export const fetchQCTaskUpload = async (
  clinicalTrialId: string,
  taskId: string,
  uploadId: string
) => {
  if (USE_API_MOCKS) {
    await fetchQCTaskUploadMock(taskId, uploadId);
  }

  return api.get<{ uploads: QCTaskUpload[] }>(
    replacePathParams(API_URL.qcTaskUpload, { taskId, uploadId }),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-upload',
    }
  );
};

export const fetchQCTaskComments = async (
  clinicalTrialId: string,
  taskId: string
) => {
  if (USE_API_MOCKS) {
    await fetchQCTaskCommentsMock(taskId);
  }

  return api.get<QCTaskComment[]>(
    API_URL.qcTaskComments.replace(':taskId', taskId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-comments',
    }
  );
};

export const addQCTaskComments = async (
  clinicalTrialId: string,
  taskId: string,
  message: string
) => {
  if (USE_API_MOCKS) {
    await addQCTaskCommentsMock(taskId, message);
  }

  return api.post<QCTaskComment>(
    API_URL.qcTaskComments.replace(':taskId', taskId),
    {
      message,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-add-comment',
    }
  );
};

export const qcTaskAssignToMe = (clinicalTrialId: string, taskId: string) => {
  return api.put(
    API_URL.qcTaskAssignToMe.replace(':taskId', taskId),
    {},
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-assign-to-me',
    }
  );
};

export const fetchQCTaskSeries = async (
  clinicalTrialId: string,
  taskId: string
) => {
  if (USE_API_MOCKS) {
    await fetchQCTaskSeriesMock(taskId);
  }

  return api.get<{ visits: QCVisitWithSeries[] }>(
    API_URL.qcTaskSeries.replace(':taskId', taskId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-series',
    }
  );
};

export const fetchQCTaskPatient = async (
  clinicalTrialId: string,
  taskId: string
) => {
  return api.get<QCTaskPatient>(
    API_URL.qcTaskPatient.replace(':taskId', taskId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-patient',
    }
  );
};

export const fetchQCTaskUploadEdtf = async (
  clinicalTrialId: string,
  taskId: string,
  uploadId: string
) => {
  return api.get<QCUploadEDTF>(
    API_URL.qcTaskUploadEdtf
      .replace(':taskId', taskId)
      .replace(':uploadId', uploadId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-upload-edtf',
    }
  );
};

export const qcTaskStatus = (
  clinicalTrialId: string,
  taskId: string,
  status: QcStatusEnum
) => {
  return api.put(
    API_URL.qcTaskStatus.replace(':taskId', taskId),
    {
      status,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-status',
    }
  );
};

export const setQcTaskPatientStatus = async (
  clinicalTrialId: string,
  taskId: string,
  status: PatientUploadStatusEnum
) => {
  return api.put(
    API_URL.qcPatientStatus.replace(':taskId', taskId),
    {
      updatedStatus: status,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-patient-status',
    }
  );
};

export const fetchQCTaskUploadSendToWorkStation = async (
  clinicalTrialId: string,
  taskId: string,
  uploadId: string
) => {
  return api.post(
    replacePathParams(API_URL.qcUploadSendToWorkStation, { taskId, uploadId }),
    {},
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-task-upload-send-to-work-station',
    }
  );
};

export const updatePatientDob = async (
  clinicalTrialId: string,
  id: string,
  dob: string) => {
  return api.put(
    API_URL.qcUpdatePatientDob.replace(':id', id),
    { dob },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-patient-update-dob',
    }
  );
};

export const updatePatientSite = async (
  clinicalTrialId: string,
  id: string,
  siteId: string) => {
  return api.put(
    API_URL.qcUpdatePatientSite.replace(':id', id),
    { siteId },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-patient-update-site',
    }
  );
};

export const updatePatientCode = async (
  clinicalTrialId: string,
  id: string,
  patientCode: string) => {
  return api.put(
    API_URL.qcUpdatePatientCode.replace(':id', id),
    { patientCode },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      id: 'qc-patient-update-patient-code',
    }
  );
};
